package com.iregained.site.components.sections.product.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.product.styles.FeatureListItemStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.theme.unextractable
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Cursor.Companion.Default
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Smooth Rise
@Composable
private fun Modifier.smoothRise(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else 50.px).transition(
        Transition.of("opacity", 0.9.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.9.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun ProductHowItWorksSection(data: ProductHowItWorksSectionData = defaultProductHowItWorksData) {
    val palette = ColorMode.current.toSitePalette()
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "product-how-it-works-section"
    var isVisible by remember { mutableStateOf(false) }

    if (data.pillars.isNotEmpty()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(Colors.White)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                    .justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center).toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.sectionHeader.isNotBlank()) {
                        SectionHeader(
                            text = data.sectionHeader,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 40.px).smoothRise(isVisible, 0),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    if (data.sectionSubtext.isNotBlank()) {
                        Div(
                            attrs = SubheadlineTextStyle.toModifier().maxWidth(1440.px).fontSize(18.px)
                                .fontWeight(FontWeight.Medium).color(palette.custom.darkText).margin(bottom = 40.px)
                                .smoothRise(isVisible, 200).toAttrs {
                                    ref { htmlElement ->
                                        htmlElement.innerHTML = data.sectionSubtext; onDispose {}
                                    }
                                })
                    }

                    Column(modifier = Modifier.fillMaxWidth().gap(100.px)) {
                        data.pillars.forEachIndexed { index, pillar ->
                            key(pillar.title) {
                                Box(modifier = Modifier.fillMaxWidth().smoothRise(isVisible, 400 + (index * 200))) {
                                    ArchitectureRow(
                                        imageSrc = pillar.imageSrc,
                                        title = pillar.title,
                                        description = pillar.description,
                                        features = pillar.features,
                                        bulletIconPath = data.bulletIconPath,
                                        imageOnLeft = index % 2 == 0
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ArchitectureRow(
    imageSrc: String,
    title: String,
    description: String,
    features: List<String>,
    bulletIconPath: String,
    imageOnLeft: Boolean
) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG

    if (isDesktop) {
        Row(modifier = Modifier.fillMaxWidth().gap(100.px), verticalAlignment = Alignment.CenterVertically) {
            if (imageOnLeft) {
                ComponentVisual(imageSrc, Modifier.weight(1f).width(0.px))
                ComponentDetails(title, description, features, bulletIconPath, Modifier.weight(1f).width(0.px))
            } else {
                ComponentDetails(title, description, features, bulletIconPath, Modifier.weight(1f).width(0.px))
                ComponentVisual(imageSrc, Modifier.weight(1f).width(0.px))
            }
        }
    } else {
        Column(modifier = Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
            ComponentVisual(imageSrc, Modifier.fillMaxWidth().margin(bottom = 25.px))
            ComponentDetails(title, description, features, bulletIconPath, Modifier.fillMaxWidth(), isMobile = true)
        }
    }
}

@Composable
private fun ComponentVisual(src: String, modifier: Modifier) {
    if (src.isNotBlank()) {
        Box(
            modifier = modifier.minWidth(0.px).styleModifier { property("aspect-ratio", "16 / 9") }.borderRadius(16.px)
            .styleModifier { property("overflow", "hidden") }) {
            Image(src = src, modifier = Modifier.unextractable().fillMaxSize().objectFit(ObjectFit.Cover))
        }
    }
}

@Composable
private fun ComponentDetails(
    title: String,
    description: String,
    features: List<String>,
    bulletIconPath: String,
    modifier: Modifier,
    isMobile: Boolean = false
) {
    val palette = ColorMode.current.toSitePalette()
    val align = if (isMobile) Alignment.CenterHorizontally else Alignment.Start
    val textAlign = if (isMobile) TextAlign.Center else TextAlign.Start

    Column(modifier = modifier, horizontalAlignment = align) {
        if (title.isNotBlank()) SectionHeader(
            text = title,
            fontSize = 24.px,
            lineWidthPercent = 25.percent,
            modifier = Modifier.margin(bottom = 16.px),
            alignment = align
        )
        if (description.isNotBlank()) Div(
            attrs = SubheadlineTextStyle.toModifier().fontSize(18.px)
            .color(palette.custom.darkText).textAlign(textAlign).margin(bottom = 24.px).styleModifier {
                property("display", "-webkit-box"); property(
                "-webkit-line-clamp", "4"
            ); property("-webkit-box-orient", "vertical"); property("overflow", "hidden")
            }.toAttrs { ref { htmlElement -> htmlElement.innerHTML = description; onDispose {} } })
        if (features.isNotEmpty()) {
            Column(modifier = Modifier.gap(8.px)) {
                features.forEach { feature ->
                    key(feature) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = FeatureListItemStyle.toModifier().cursor(Default)
                        ) {
                            if (bulletIconPath.isNotBlank()) Image(
                                src = bulletIconPath,
                                modifier = Modifier.unextractable().size(16.px).margin(right = 10.px)
                            )
                            SpanText(text = feature, modifier = Modifier.fontWeight(FontWeight.Medium))
                        }
                    }
                }
            }
        }
    }
}

/**
 * CMS IMAGE UPLOAD SPECS:
 * @field imageSrc (Inside PillarData):
 * - Aspect Ratio: 16:9.
 * - Recommended Dimensions: 1280x720px (min 960x540).
 * - Max File Size: 250KB (WebP or JPEG).
 * - Behavior: Image fills its column width and is cropped to a 16/9 ratio
 *   (ObjectFit.Cover) so every row has the exact same visual height.
 * @field bulletIconPath:
 * - Aspect Ratio: 1:1.
 * - Recommended Dimensions: 16x16px.
 * - Format: SVG ONLY.
 */
data class HowItWorksData(
    val imageSrc: String, val title: String, val description: String, val features: List<String>
)

data class ProductHowItWorksSectionData(
    val sectionId: String? = null,
    val sectionHeader: String,
    val sectionSubtext: String,
    val bulletIconPath: String,
    val pillars: List<HowItWorksData>
)

val defaultProductHowItWorksData = ProductHowItWorksSectionData(
    sectionId = "product-how-it-works-section",
    sectionHeader = "KEY ADVANTAGES OF MyHand<sup>®</sup> SYSTEM",
    sectionSubtext = "The MyHand<sup>®</sup> System leverages the brain's innate neurophysiological mechanisms to \"reprogram\" damaged neural pathways. Developed in collaboration with clinicians and stroke survivors, this clinically proven system facilitates the development of alternative neural pathways by integrating the human sensory system with the motor apparatus.\n\nThe system achieves this by actively engaging the patient’s:\n" + "<b>-> Sensory System:</b> Utilizing touch, pressure, and proprioception.\n" + "<b>-> Motor Capabilities:</b> Activating specific muscles throughout the hand and forearm.\n",
    bulletIconPath = Res.Images.ProductPage.Neuroscience.ICON_CHECK,
    pillars = listOf(
        HowItWorksData(
            imageSrc = Res.Images.ProductPage.TechnologyPillars.HARDWARE_DEVICE,
            title = "The MyHand<sup>®</sup> System Hardware",
            description = "The MyHand<sup>®</sup> System is a targeted rehabilitation tool for people with hand impairments...",
            features = listOf("Multi-grip & Pinch", "Force Feedback", "Active / Passive / Assistive", "Quick Setup")
        ), HowItWorksData(
            imageSrc = Res.Images.ProductPage.TechnologyPillars.SOFTWARE_APP,
            title = "The MyHand<sup>®</sup> System App",
            description = "It includes a built-in assessment protocol to streamline evaluations...",
            features = listOf(
                "Built-in Patient Assessment", "Pre-programmed Protocols", "Gamification", "Real Time Data"
            )
        ), HowItWorksData(
            imageSrc = Res.Images.ProductPage.TechnologyPillars.DASHBOARD,
            title = "The MyHand<sup>®</sup> System Dashboard",
            description = "A secure cloud-based platform for clinicians. It enables remote monitoring...",
            features = listOf("Manage Clinic & Patient Assignments", "Clinician Telemedicine", "Analytics & Reporting")
        )
    )
)